import { useEffect, useRef, useState } from 'react';
import { POSES, type PoseId } from '@/content/articulation';
import {
  blendShapes,
  shapeOf,
  SHOTS as CAMERA_SHOTS,
  TongueScene,
  type ShotId,
} from '@/ui/tongue3d/TongueScene';

const SHOTS: readonly ShotId[] = ['side', 'threeQuarter', 'front'];

/**
 * Development page (`?model=rBunched`): the real-time mouth model (TongueScene) with its controls:
 * a pose, how far into it (0 = rest), the camera shot, and the gap to the roof of the mouth.
 */
export function ModelPreview({ initial }: { initial: PoseId }) {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<TongueScene | null>(null);
  const [pose, setPose] = useState<PoseId>(initial);
  const [weight, setWeight] = useState(1);
  const [shot, setShot] = useState<ShotId>(
    (new URLSearchParams(window.location.search).get('shot') as ShotId | null) ?? 'side',
  );
  const [gap, setGap] = useState('');

  useEffect(() => {
    if (!host.current) return;
    const style =
      new URLSearchParams(window.location.search).get('style') === 'cutaway' ? 'cutaway' : 'xray';
    const s = new TongueScene(host.current, { style });
    scene.current = s;
    s.setGapMarker(new URLSearchParams(window.location.search).has('gap'));
    // `?render`: frame-by-frame control for recording videos (scripts set exact shapes per frame).
    if (new URLSearchParams(window.location.search).has('render'))
      (window as unknown as { __render: unknown }).__render = {
        scene: s,
        shapeOf,
        blendShapes,
        SHOTS: CAMERA_SHOTS,
      };
    // `?demo`: a scripted loop, as a lesson would direct the model.
    const run = { on: true };
    if (new URLSearchParams(window.location.search).has('demo')) {
      const wait = (ms: number) =>
        new Promise((resolve) => {
          setTimeout(resolve, ms);
        });
      void (async () => {
        await s.ready;
        s.setBlend('rest', 'rest', 1);
        while (run.on) {
          await s.setShot('side', 800);
          await wait(800);
          await s.moveTo('rBunched', 1600);
          await wait(900);
          await s.setShot('threeQuarter', 2200);
          await wait(700);
          await s.moveTo('rest', 1400);
          await s.setShot('side', 1800);
          await s.moveTo('w', 1300);
          await wait(900);
          await s.moveTo('rest', 1200);
          await wait(600);
        }
      })();
    }
    return () => {
      run.on = false;
      s.dispose();
    };
  }, []);
  useEffect(() => {
    const s = scene.current;
    if (!s) return;
    s.setBlend('rest', pose, weight);
    const g = s.gap();
    setGap(`${g.mm.toFixed(1)} mm at z ${g.z.toFixed(0)}`);
  }, [pose, weight]);
  useEffect(() => {
    void scene.current?.setShot(shot);
  }, [shot]);

  return (
    <main style={{ padding: 16, background: '#0b1a3a', minHeight: '100vh', color: 'white' }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        {(Object.keys(POSES) as PoseId[]).map((id) => (
          <button
            key={id}
            type="button"
            aria-pressed={id === pose}
            onClick={() => {
              setPose(id);
            }}
          >
            {id}
          </button>
        ))}
        <label>
          weight{' '}
          <input
            type="range"
            min={0}
            max={1.3}
            step={0.01}
            value={weight}
            onChange={(e) => {
              setWeight(Number(e.target.value));
            }}
          />
        </label>
        {SHOTS.map((id) => (
          <button
            key={id}
            type="button"
            aria-pressed={id === shot}
            onClick={() => {
              setShot(id);
            }}
          >
            {id}
          </button>
        ))}
        <span data-testid="gap">gap {gap}</span>
      </div>
      <div
        ref={host}
        style={{
          width: '100%',
          maxWidth: 900,
          aspectRatio: '4 / 3',
          background: '#e8f0fb',
          borderRadius: 16,
        }}
      />
    </main>
  );
}
